import { Box, styled } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { AI, PURPLE } from 'theme/prismDesignTokens';

/**
 * Footnote badge treatment (SCAL-8969). Two independent axes:
 *   - review status: PENDING gets the review purple fill; accepted/legacy keep
 *     the primary look (dismissed badges never render - filtered upstream);
 *   - provenance: an AI-CREATED citation gets a purple border (the pending-review
 *     purple, matching the PDF highlight outline), analyst badges stay borderless.
 */
export const citationBadgeSx = ({
  pending,
  aiCreated,
  active,
  wide,
}: {
  pending: boolean;
  aiCreated: boolean;
  active: boolean;
  wide: boolean;
}): SxProps<Theme> => ({
  maxWidth: wide ? '40px' : '24px',
  cursor: 'pointer',
  ...(pending
    ? {
      backgroundColor: active ? PURPLE[500] : PURPLE[100],
      color: active ? 'common.white' : PURPLE[600],
    }
    : {
      backgroundColor: active ? 'primary.main' : 'primary.light',
      color: active ? 'primary.contrastText' : 'primary.dark',
    }),
  ...(aiCreated && { border: `1px solid ${AI}` }),
});

export const StyledCitationValueViewerWrapper = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  padding: '2px 4px',
}));

export const StyledCitationNumber = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '14px',
  width: '16px',
  height: '18px',
  fontSize: '0.6rem',
  fontWeight: 600,
  padding: '0 2px',
  borderRadius: '2px',
  flexShrink: 0,
}));

export const StyledCitationNumberWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'offsetLeft',
})<{ offsetLeft?: number }>(({ offsetLeft = 2 }) => ({
  position: 'absolute',
  // Default 2px from the cell's left edge. When a Pre-FX currency chip is
  // present (cap-table OIP), the host bumps this so the citation footnote sits
  // to the RIGHT of the always-leftmost chip instead of overlapping it
  // (SCAL-9062).
  left: offsetLeft,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: '2px',
  zIndex: 10,
  pointerEvents: 'auto',
}))

export const StyledCitationValueViewer = styled(Box, {
  shouldForwardProp: prop => prop !== 'reservedBadgeWidth',
})<{ reservedBadgeWidth?: number }>(({ reservedBadgeWidth = 0 }) => ({
  // position: relative so an absolutely-positioned inner control (the date
  // picker Root) anchors to this padded box rather than the cell, letting the
  // reserved gutter actually shift it right (SCAL-8987).
  position: 'relative',
  flex: 1,
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Dropdown and date-picker cells render their own control inside the cell, so
  // the badge cannot overlay them. Reserve room on the left for the badge gutter
  // and shift the control right instead of letting them fight (SCAL-8987).
  ...(reservedBadgeWidth > 0 && { paddingLeft: `${reservedBadgeWidth}px` }),
}))